import { useState, useRef, useEffect, useLayoutEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

import InfoBlock from 'components/common/InfoBlock'
import { getVenderWelcomeInfo } from 'utils/helpers/vender-welcome-info'

const collapsedLineCount = 2
const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect

const DiscountInfoBlock = ({
  vender,
  expanded: controlledExpanded,
  onExpandedChange,
  ...rest
}) => {
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(false)
  const [isOverflow, setIsOverflow] = useState(false)
  const textRef = useRef(null)

  const { favorableDiscountText: discountText } = getVenderWelcomeInfo(vender)
  const expanded = controlledExpanded ?? uncontrolledExpanded
  const toggleExpanded = () => {
    const nextExpanded = !expanded

    if (onExpandedChange) {
      onExpandedChange(nextExpanded)
    } else {
      setUncontrolledExpanded(nextExpanded)
    }
  }

  useIsomorphicLayoutEffect(() => {
    const textElement = textRef.current
    if (!discountText || !textElement) return

    const updateOverflow = () => {
      const style = window.getComputedStyle(textElement)
      const fontSize = parseFloat(style.fontSize) || 14
      const lineHeight = parseFloat(style.lineHeight) || fontSize * 1.5
      const collapsedHeight = lineHeight * collapsedLineCount

      setIsOverflow(textElement.scrollHeight > collapsedHeight + 1)
    }

    updateOverflow()

    if (typeof window.ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateOverflow)
      return () => window.removeEventListener('resize', updateOverflow)
    }

    const resizeObserver = new window.ResizeObserver(updateOverflow)
    resizeObserver.observe(textElement)

    return () => resizeObserver.disconnect()
  }, [discountText])

  if (!discountText) return null

  return (
    <InfoBlock px={5} py={5} $radius={6} {...rest} sx={{ height: 'auto' }}>
      <Box display="flex" flexWrap="nowrap" alignItems="center">
        <Typography>
          <Box color="text.minus2" fontWeight="fontWeightBold">
            特色優惠
          </Box>
        </Typography>
      </Box>
      <Typography
        ref={textRef}
        component="div"
        variant="paraBodySmall"
        color="text.minus2"
        sx={{
          mt: 2,
          lineHeight: 1.5,
          display: '-webkit-box',
          WebkitLineClamp: expanded ? 'unset' : collapsedLineCount,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
      >
        {discountText}
      </Typography>
      {(isOverflow || expanded) && (
        <Box
          display="flex"
          alignItems="center"
          onClick={toggleExpanded}
          width="fit-content"
          mt={2}
          sx={{ cursor: 'pointer' }}
        >
          <Typography variant="paraBodySmall" color="primary.mainPlus2">
            {expanded ? '收合' : '展開'}
          </Typography>
          {expanded ? (
            <ExpandLessIcon sx={{ color: 'primary.mainPlus2', fontSize: 24 }} />
          ) : (
            <ExpandMoreIcon sx={{ color: 'primary.mainPlus2', fontSize: 24 }} />
          )}
        </Box>
      )}
    </InfoBlock>
  )
}

export default DiscountInfoBlock
